import { type Static, type StaticEncode, Type } from '@sinclair/typebox';
import { CHANNEL_DESCRIPTION_MAX_LENGTH, CHANNEL_TITLE_MAX_LENGTH, ChannelDTOSchema } from '../dto/channel.dto.js';
import { ITEM_DESCRIPTION_MAX_LENGTH, ITEM_TITLE_MAX_LENGTH, ItemDTOSchema } from '../dto/item.dto.js';
import { PODCAST_AUTHOR_MAX_LENGTH, type PodcastDTO, PodcastDTOSchema } from '../dto/podcast.dto.js';
import { type PodcastEpisodeDTO, PodcastEpisodeDTOSchema } from '../dto/podcast-episode.dto.js';

const URL_REGEX =
  '^(?:(https?|ftp):\\/\\/)?(?:localhost|127(?:.[0-9]{1,3}){3}|[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*)(?::d{1,5})?(?:/[^s]*)?$';
const SlugSchema = Type.String({ pattern: '^[a-z0-9]+(?:-[a-z0-9]+)*$', minLength: 1, maxLength: 100 });

// =====================================================================================================================
// Check Slug Availability
// =====================================================================================================================

export const CheckSlugAvailabilityQuerySchema = Type.Object({ slug: SlugSchema });
export type CheckSlugAvailabilityQuery = Static<typeof CheckSlugAvailabilityQuerySchema>;

export const CheckSlugAvailabilityReplySchema = Type.Object({ available: Type.Boolean() });
export type CheckSlugAvailabilityReply = Static<typeof CheckSlugAvailabilityReplySchema>;

// =====================================================================================================================
// Claim Podcast
// =====================================================================================================================

export const ClaimPodcastBodySchema = Type.Object({ feedUrl: Type.String({ pattern: URL_REGEX }) });
export type ClaimPodcastBody = Static<typeof ClaimPodcastBodySchema>;

// =====================================================================================================================
// Confirm Claim
// =====================================================================================================================

export const ConfirmClaimBodySchema = Type.Object({
  confirmationCode: Type.Number({ maxLength: 6, minLength: 6 }),
  feedUrl: Type.String({ pattern: URL_REGEX }),
});
export type ConfirmClaimBody = Static<typeof ConfirmClaimBodySchema>;

// =====================================================================================================================
// Create Podcast
// =====================================================================================================================

export const CreatePodcastBodySchema = Type.Object({
  author: Type.String({ minLength: 1, maxLength: PODCAST_AUTHOR_MAX_LENGTH }),
  description: Type.String({ minLength: 1, maxLength: CHANNEL_DESCRIPTION_MAX_LENGTH }),
  title: Type.String({ minLength: 1, maxLength: CHANNEL_TITLE_MAX_LENGTH }),
  categories: Type.Optional(PodcastDTOSchema.properties.categories),
});
export type CreatePodcastBody = Static<typeof CreatePodcastBodySchema>;

export const CreatePodcastReplySchema = PodcastDTOSchema;
export type CreatePodcastReply = Static<typeof CreatePodcastReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const UpdatePodcastInfoParamsSchema = Type.Object({ channelId: Type.String() });
export type UpdatePodcastInfoParams = Static<typeof UpdatePodcastInfoParamsSchema>;

export const UpdatePodcastInfoBodySchema = Type.Object({
  author: Type.Optional(Type.String({ maxLength: 100 })),
  title: Type.Optional(ChannelDTOSchema.properties.title),
  description: Type.Optional(ChannelDTOSchema.properties.description),
  categories: Type.Optional(PodcastDTOSchema.properties.categories),
  image: Type.Optional(Type.Any()),
});
export type UpdatePodcastInfoBody = Static<typeof UpdatePodcastInfoBodySchema>;

export const UpdatePodcastInfoReplySchema = PodcastDTOSchema;
export type UpdatePodcastInfoReply = PodcastDTO;

// ---------------------------------------------------------------------------------------------------------------------

export const UpdatePodcastEpisodeInfoParamsSchema = Type.Object({ channelId: Type.String(), itemId: Type.String() });
export type UpdatePodcastEpisodeInfoParams = Static<typeof UpdatePodcastEpisodeInfoParamsSchema>;

export const UpdatePodcastEpisodeInfoBodySchema = Type.Object({
  description: Type.Optional(Type.String({ maxLength: ITEM_DESCRIPTION_MAX_LENGTH })),
  image: Type.Optional(Type.Any()),
  title: Type.String({ maxLength: ITEM_TITLE_MAX_LENGTH }),
});
export type UpdatePodcastEpisodeInfoBody = Static<typeof UpdatePodcastEpisodeInfoBodySchema>;

export const UpdatePodcastEpisodeInfoReplySchema = PodcastEpisodeDTOSchema;
export type UpdatePodcastEpisodeInfoReply = PodcastEpisodeDTO;

// ---------------------------------------------------------------------------------------------------------------------

export const UpdatePodcastConfigBodySchema = Type.Object({
  channelId: Type.String(),
  config: Type.Intersect([
    Type.Omit(Type.Index(PodcastDTOSchema, ['config']), ['defaultItemsVisibility']),
    Type.Object({
      defaultItemsVisibility: Type.String(),
    }),
  ]),
});
export type UpdatePodcastConfigBody = Static<typeof UpdatePodcastConfigBodySchema>;

export const UpdatePodcastConfigReplySchema = Type.Pick(PodcastDTOSchema, ['config']);
export type UpdatePodcastConfigReply = Static<typeof UpdatePodcastConfigReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const UpdateItemsVisibilityBodySchema = Type.Composite([
  Type.Object({
    itemIds: Type.Array(Type.String()),
    visibility: Type.Union([Type.Literal('public'), Type.Literal('hidden'), Type.String()]),
  }),
  Type.Object({ channelId: Type.String() }),
]);
export type UpdateItemsVisibilityBody = Static<typeof UpdateItemsVisibilityBodySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const UpdateSlugBodySchema = Type.Object({ channelId: Type.String(), slug: SlugSchema });
export type UpdateSlugBody = Static<typeof UpdateSlugBodySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const UploadPodcastEpisodeQuerystringSchema = Type.Object({ channelId: Type.String() });
export type UploadPodcastEpisodeQuerystring = Static<typeof UploadPodcastEpisodeQuerystringSchema>;

export const UploadPodcastEpisodeBodySchema = Type.Pick(ItemDTOSchema, ['title', 'description']);
export type UploadPodcastEpisodeBody = StaticEncode<typeof UploadPodcastEpisodeBodySchema>;

export const UploadPodcastEpisodeReplySchema = PodcastEpisodeDTOSchema;
export type UploadPodcastEpisodeReply = Static<typeof UploadPodcastEpisodeReplySchema>;

// =====================================================================================================================
// Delete Podcast Episode
// =====================================================================================================================

export const DeletePodcastEpisodesBodySchema = Type.Object({ itemIds: Type.Array(Type.String(), { minItems: 1 }) });
export type DeletePodcastEpisodesBody = Static<typeof DeletePodcastEpisodesBodySchema>;

export const DeletePodcastEpisodeParamsSchema = Type.Object({ channelId: Type.String() });
export type DeletePodcastEpisodeParams = Static<typeof DeletePodcastEpisodeParamsSchema>;
